/**
 * useShareVisitTracker — fires ONCE per page load when the URL carries
 * an `utm_source` query param (set by ShareBar links).  Pings the
 * backend so admins can see which channel drove the click.
 *
 * Behaviour:
 *  • Reads utm_source / utm_medium / utm_campaign / utm_content from URL
 *  • Optional explicit vehicleId override (when caller knows it)
 *  • Records a sessionStorage flag so a single browser tab doesn't
 *    re-fire on internal navigation back to the page
 *  • Silently swallows network errors — tracking must never break the UX
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { apiClient } from "./api";

export function useShareVisitTracker(vehicleId) {
  const location = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const params = new URLSearchParams(location.search);
      const utm_source = params.get("utm_source");
      if (!utm_source) return;

      const utm_medium = params.get("utm_medium") || "social";
      const utm_campaign = params.get("utm_campaign") || "share_listing";
      const utm_content = params.get("utm_content") || vehicleId || "";

      // de-dup within the same tab session
      const flag = `cartal:utm:${utm_source}:${utm_content || location.pathname}`;
      if (window.sessionStorage.getItem(flag)) return;
      window.sessionStorage.setItem(flag, "1");
      // iter328 — Also mark the global first-touch flag so the
      // app-wide TrafficSourceTracker doesn't re-fire on the very
      // next route change (would produce a duplicate row with
      // vehicle_id=null and inflate "total visits" by 2x per share
      // landing on a vehicle page).
      try { window.sessionStorage.setItem("cartal:first_touch:v1", "1"); } catch (_e) { /* ignore */ }

      apiClient
        .post("/analytics/share-visits", {
          utm_source,
          utm_medium,
          utm_campaign,
          vehicle_id: vehicleId || utm_content || null,
          path: location.pathname,
          referrer: document.referrer || null,
        })
        .catch(() => { /* silent */ });
    } catch {
      /* private-mode / storage errors — ignore */
    }
  }, [location.pathname, location.search, vehicleId]);
}

export default useShareVisitTracker;
